import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TableSortLabel, Chip, Skeleton, MenuItem,
    Select, Container, Paper, Button, IconButton, Tooltip,
} from '@mui/material';
import { ArrowBack, PlayArrow } from '@mui/icons-material';
import NavBar from '../../../Components/NavBar/navbar';
import ArcadeHubNav, { arcadeHubPath, ARCADE_SECTIONS } from '../ArcadeHubNav';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import { gamesData } from '../../../Components/Arcade/gamesConfig';
import {
    buildGameNameMap, gameDisplayName, resolveGameLabel, resolveUserLabel,
    formatAgentLabel, formatRunSource, isHumanRunSource, leaderboardTableColumns,
} from './displayHelpers';
import { EmptyState, ErrorState } from '../../../Components/Arcade/ui/StateViews';
import usePageTitle from '../../../Components/Arcade/ui/usePageTitle';

function sortRows(rows, orderBy, order) {
    if (!orderBy) return rows;
    return [...rows].sort((a, b) => {
        const av = a[orderBy];
        const bv = b[orderBy];
        if (av === undefined || av === null) return 1;
        if (bv === undefined || bv === null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') {
            return order === 'asc' ? av - bv : bv - av;
        }
        return order === 'asc'
            ? String(av).localeCompare(String(bv))
            : String(bv).localeCompare(String(av));
    });
}

function LeaderboardTable({ rows, loading, gameNameMap = {}, showGameColumn = false }) {
    const [orderBy, setOrderBy] = useState('score');
    const [order, setOrder] = useState('desc');

    const handleSort = (col) => {
        if (orderBy === col) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(col);
            setOrder('desc');
        }
    };

    const sorted = useMemo(() => sortRows(rows, orderBy, order), [rows, orderBy, order]);
    const columns = leaderboardTableColumns(showGameColumn);

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="text" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                ))}
            </Box>
        );
    }

    if (!rows.length) {
        return <EmptyState message="No submissions yet for this game." />;
    }

    return (
        <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.key} sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderBottom: `1px solid ${THEME.border}` }}>
                                {['score', 'timestamp'].includes(col.key) ? (
                                    <TableSortLabel
                                        active={orderBy === col.key}
                                        direction={orderBy === col.key ? order : 'desc'}
                                        onClick={() => handleSort(col.key)}
                                        sx={{ color: `${THEME.secondary} !important`, '&.Mui-active': { color: `${THEME.primary} !important` } }}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                ) : col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map((row, idx) => {
                        const runId = row.run_id || row._id || row.id;
                        return (
                            <TableRow key={runId || idx} hover>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>{idx + 1}</TableCell>
                                <TableCell sx={{ color: 'white', borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.agent_id && !isHumanRunSource(row.source) ? (
                                        <Link to={`/arcade/dashboard/agents/${row.agent_id}`} style={{ color: 'white', textDecoration: 'none' }}>
                                            {formatAgentLabel(row)}
                                        </Link>
                                    ) : (formatAgentLabel(row, row.player || row.player_id))}
                                </TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.user_id ? (
                                        <Link to={`/arcade/dashboard/users/${row.user_id}`} style={{ color: THEME.secondary, textDecoration: 'none' }}>
                                            {resolveUserLabel(row)}
                                        </Link>
                                    ) : resolveUserLabel(row)}
                                </TableCell>
                                {showGameColumn && (
                                    <TableCell sx={{ color: 'white', borderBottom: `1px solid ${THEME.grid}` }}>
                                        {resolveGameLabel(row, gameNameMap)}
                                    </TableCell>
                                )}
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    <Chip size="small" label={row.score ?? 0} sx={{ bgcolor: 'transparent', border: `1px solid ${THEME.primary}`, color: THEME.primary, fontWeight: 'bold' }} />
                                </TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>{formatRunSource(row.source || (row.agent_id ? 'agent' : 'human'))}</TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.timestamp ? new Date(row.timestamp).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    {runId ? (
                                        <Tooltip title="Play">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                to={`/arcade/replay/${runId}`}
                                                aria-label="Play"
                                                sx={{ color: THEME.primary }}
                                            >
                                                <PlayArrow fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : '—'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/**
 * Dual-purpose component:
 * - Embedded in DashboardHome as a "Games" tab (props: games, leaderboard, loading)
 * - Standalone routed page at /arcade/dashboard/games/:gameId (fetches its own data)
 */
const GameLeaderboardView = ({ games = [], leaderboard = null, loading: loadingProp = false }) => {
    const { gameId: routeGameId } = useParams();
    usePageTitle(routeGameId ? `Leaderboard – ${routeGameId}` : 'Dashboard');
    const isStandalone = Boolean(routeGameId);

    const [selectedGame, setSelectedGame] = useState(routeGameId || '');
    const [rows, setRows] = useState(leaderboard || []);
    const [loading, setLoading] = useState(loadingProp);
    const [error, setError] = useState(null);
    const [standaloneGames, setStandaloneGames] = useState([]);

    // Embedded mode: just group/display the leaderboard passed in, filterable by a dropdown
    const gameNameMap = useMemo(
        () => buildGameNameMap([...(games.length ? games : standaloneGames), ...gamesData]),
        [games, standaloneGames]
    );

    const gameOptions = useMemo(() => {
        const fromLeaderboard = new Set((leaderboard || []).map((e) => e.game_id || e.game).filter(Boolean));
        const fromGames = (games.length ? games : standaloneGames).map((g) => g._id || g.id).filter(Boolean);
        return Array.from(new Set([...fromGames, ...fromLeaderboard])).sort();
    }, [games, standaloneGames, leaderboard]);

    useEffect(() => {
        if (isStandalone) {
            let mounted = true;
            async function fetchGameLeaderboard() {
                setLoading(true);
                setError(null);
                try {
                    const [res, gamesRes] = await Promise.all([
                        fetch(API_ENDPOINTS.LEADERBOARD(routeGameId)),
                        fetch(API_ENDPOINTS.GAMES_LIST),
                    ]);
                    if (!res.ok) throw new Error(`status ${res.status}`);
                    const data = await res.json();
                    const list = data.leaderboard || data.entries || (Array.isArray(data) ? data : []);
                    if (mounted) setRows(list);
                    if (gamesRes.ok) {
                        const g = await gamesRes.json();
                        if (mounted) setStandaloneGames(Array.isArray(g) ? g : (g.games || []));
                    }
                } catch (e) {
                    if (mounted) setError('Failed to load');
                    if (mounted) setRows([]);
                }
                if (mounted) setLoading(false);
            }
            fetchGameLeaderboard();
            return () => { mounted = false; };
        } else {
            setRows(leaderboard || []);
            setLoading(loadingProp);
        }
    }, [isStandalone, routeGameId, leaderboard, loadingProp]);

    const filteredRows = useMemo(() => {
        if (!selectedGame) return rows;
        return rows.filter((r) => (r.game_id || r.game) === selectedGame);
    }, [rows, selectedGame]);

    const content = (
        <>
            {!isStandalone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                        Game Leaderboards
                    </Typography>
                    <Select
                        size="small"
                        value={selectedGame}
                        onChange={(e) => setSelectedGame(e.target.value)}
                        displayEmpty
                        sx={{ color: 'white', minWidth: 200, '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME.border } }}
                    >
                        <MenuItem value="">All Games</MenuItem>
                        {gameOptions.map((g) => (
                            <MenuItem key={g} value={g}>{gameDisplayName(g, gameNameMap)}</MenuItem>
                        ))}
                    </Select>
                </Box>
            )}
            {error && <ErrorState message="This leaderboard can't be shown right now. Please try again in a moment." />}
            {!error && (
                <LeaderboardTable
                    rows={filteredRows}
                    loading={loading}
                    gameNameMap={gameNameMap}
                    showGameColumn={!isStandalone}
                />
            )}
        </>
    );

    if (!isStandalone) return content;

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="lg">
                    <ArcadeHubNav activeSection={ARCADE_SECTIONS.dashboard} />
                    <Button component={Link} to={arcadeHubPath(ARCADE_SECTIONS.dashboard)} startIcon={<ArrowBack />} sx={{ color: THEME.secondary, mb: 3 }}>
                        Back to Dashboard
                    </Button>
                    <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 3 }}>
                        {gameDisplayName(routeGameId, gameNameMap)} <span style={{ color: THEME.primary }}>Leaderboard</span>
                    </Typography>
                    <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, p: 2 }}>
                        {content}
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default GameLeaderboardView;
